import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import { ThemeProvider } from "./context/ThemeContext";
import LandingPage from "./pages/LandingPage";
import AboutPage from "./pages/AboutPage";
import ConsultationPage from "./pages/ConsultationPage";
import ContactPage from "./pages/ContactPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import AdminDashboard from "./pages/AdminDashboard";
import ClientDashboard from "./pages/ClientDashboard";
import ReferrerDashboard from "./pages/ReferrerDashboard";
import FundManagement from "./pages/FundManagement";
import KYCPage from "./pages/KYCPage";
import SettingsPage from "./pages/SettingsPage";
import PaymentsPage from "./pages/PaymentsPage";
import UsersPage from "./pages/UsersPage";
import LeadsPage from "./pages/LeadsPage";
import LeadManagementPage from "./pages/LeadManagementPage";
import ContactImportPage from "./pages/ContactImportPage";
import PartnerAccountPage from "./pages/PartnerAccountPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import AnnouncementsAdminPage from "./pages/AnnouncementsAdminPage";
import HowToInvestPage from "./pages/HowToInvestPage";
import DisclaimerPage from "./pages/DisclaimerPage";
import LifestylePage from "./pages/LifestylePage";
import LifestyleAdminPage from "./pages/LifestyleAdminPage";
import MessagesPage from "./pages/MessagesPage";
import VerificationPage from "./pages/VerificationPage";
import VerifyPage from "./pages/VerifyPage";
import KYCVerificationPage from "./pages/KYCVerificationPage";
import VCRegistryPage from "./pages/VCRegistryPage";
import IntroducerProgramPage from "./pages/IntroducerProgramPage";
import RegistrationSuccessPage from "./pages/RegistrationSuccessPage";
import PrivacyPage from "./pages/PrivacyPage";
import SMSTermsPage from "./pages/SMSTermsPage";
import CommandCenterPage from "./pages/CommandCenterPage";
import PartnerAgreementPage from "./pages/PartnerAgreementPage";
import PartnerReviewPage from "./pages/PartnerReviewPage";
import PartnerFundingPage from "./pages/PartnerFundingPage";
import CommandCenterIntakePage from "./pages/CommandCenterIntakePage";
import CommandCenterConsentPage from "./pages/CommandCenterConsentPage";
import CommandCenterProfilePage from "./pages/CommandCenterProfilePage";
import AdminCommandCenterPage from "./pages/AdminCommandCenterPage";
import AdminCommandCenterFilePage from "./pages/AdminCommandCenterFilePage";
import AdminInvestorFilePage from "./pages/AdminInvestorFilePage";
import MyInvestorsPage from "./pages/MyInvestorsPage";
import InvestorDetailPage from "./pages/InvestorDetailPage";
import BrandWatermark from "./components/BrandWatermark";
import CompliancePipelinePage from "./pages/CompliancePipelinePage";
import MailboxSetupPage from "./pages/MailboxSetupPage";
import MembershipFeesPage from "./pages/MembershipFeesPage";
import BookPage from "./pages/BookPage";
import DealFlowPage from "./pages/DealFlowPage";
import AdminDealFlowPage from "./pages/AdminDealFlowPage";
import AdminPromoCodesPage from "./pages/AdminPromoCodesPage";
import AdminPartnerAccountsPage from "./pages/AdminPartnerAccountsPage";
import AdminBalanceEntryPage from "./pages/AdminBalanceEntryPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import PioneersPage from "./pages/PioneersPage";
import FundingPage from "./pages/FundingPage";
import AdminUserInvitePage from "./pages/AdminUserInvitePage";
import InviteAcceptPage from "./pages/InviteAcceptPage";
import StripePreflightPage from "./pages/StripePreflightPage";
import FinbpmNudgePage from "./pages/FinbpmNudgePage";
import BypassMiddlemanPage from "./pages/BypassMiddlemanPage";
import ActivityLogPage from "./pages/ActivityLogPage";
import SecurityStatusPage from "./pages/SecurityStatusPage";
import ParticipantConsole from "./pages/ParticipantConsole";
import LiveChat from "./components/LiveChat";
import HelpPopover from "./components/HelpPopover";
import "./App.css";

const ProtectedRoute = ({ children, allowedRoles, requiredModule, requiredFlag }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="animate-pulse-gold w-16 h-16 rounded-full border-2 border-gold"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  // Module-access gate — admins/superusers always bypass (backend already
  // returns true for every module for those roles, but this is defence-in-depth).
  if (requiredModule && !["admin", "superuser"].includes(user.role)) {
    const access = user.module_access || {};
    if (access[requiredModule] === false) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  // Launch-flag gate (STARSEED-CRM-011) — FAIL-CLOSED per FLAG-R-07: only
  // an explicit `true` on the DB-backed flag (system_settings.feature_flags,
  // served on /auth/me) opens the route. Flag absent/unreadable/false ⇒
  // bounce, for EVERY role — this is a dark-launch switch, not access
  // control, so there is deliberately no admin bypass. The backend proxy
  // enforces the same flag server-side (routes/crm_referrer.py).
  if (requiredFlag && user?.feature_flags?.[requiredFlag] !== true) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Partner route guard — mirrors ProtectedRoute but admits ONLY the external
// `partner` principal (participant-console-002, PINNED). An admin/superuser
// token does NOT satisfy it; a non-partner is bounced to its own dashboard.
const PartnerRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="animate-pulse-gold w-16 h-16 rounded-full border-2 border-gold"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role !== "partner") {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const DashboardRouter = () => {
  const { user } = useAuth();

  if (!user) return <Navigate to="/login" replace />;

  switch (user.role) {
    case "admin":
    case "superuser":
      return <AdminDashboard />;
    case "referrer":
      return <ReferrerDashboard />;
    // External partner principal has no member dashboard — its home is the
    // participant-management console (participant-console-002).
    case "partner":
      return <Navigate to="/console" replace />;
    case "client":
    default:
      return <ClientDashboard />;
  }
};

const LogoutRoute = () => {
  // forge-20260611-002: /logout previously fell through to the "*"
  // catch-all and redirected WITHOUT clearing the session — the nav kept
  // showing Dashboard. Clear auth exactly once, then leave.
  const { logout } = useAuth();
  const [done, setDone] = useState(false);
  useEffect(() => {
    logout();
    setDone(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return done ? <Navigate to="/" replace /> : null;
};

function App() {
  return (
    <LanguageProvider>
      <ThemeProvider>
      <AuthProvider>
        <div className="min-h-screen bg-obsidian noise-bg">
        <BrowserRouter>
          <BrandWatermark />
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/consultation" element={<ConsultationPage />} />
            <Route path="/disclaimer" element={<DisclaimerPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/sms-terms" element={<SMSTermsPage />} />
            <Route path="/book" element={<BookPage />} />
            <Route path="/verification" element={<VerificationPage />} />
            <Route path="/verify/:token" element={<VerifyPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/logout" element={<LogoutRoute />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/register/success" element={<RegistrationSuccessPage />} />
            <Route path="/refer/:code" element={<RegisterPage />} />
            <Route path="/pioneers" element={<PioneersPage />} />
            <Route path="/bypass-the-middleman" element={<BypassMiddlemanPage />} />
            <Route path="/funding" element={
              <ProtectedRoute>
                <FundingPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/users/invite" element={
              <ProtectedRoute allowedRoles={["admin", "superuser"]}>
                <AdminUserInvitePage />
              </ProtectedRoute>
            } />
            <Route path="/invite/:token" element={<InviteAcceptPage />} />
            <Route path="/admin/stripe/preflight" element={
              <ProtectedRoute allowedRoles={["admin", "superuser"]}>
                <StripePreflightPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/integrations/finbpm" element={
              <ProtectedRoute allowedRoles={["admin", "superuser"]}>
                <FinbpmNudgePage />
              </ProtectedRoute>
            } />
            <Route path="/admin/activity-log" element={
              <ProtectedRoute allowedRoles={["admin", "superuser"]}>
                <ActivityLogPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/security-status" element={
              <ProtectedRoute allowedRoles={["admin", "superuser"]}>
                <SecurityStatusPage />
              </ProtectedRoute>
            } />
            
            {/* Protected Routes */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <DashboardRouter />
              </ProtectedRoute>
            } />
            
            <Route path="/funds" element={
              <ProtectedRoute requiredModule="fund_tracking">
                <FundManagement />
              </ProtectedRoute>
            } />
            
            <Route path="/kyc" element={
              <ProtectedRoute>
                <KYCPage />
              </ProtectedRoute>
            } />

            <Route path="/kyc-verification" element={
              <ProtectedRoute>
                <KYCVerificationPage />
              </ProtectedRoute>
            } />

            <Route path="/admin/vc-registry" element={
              <ProtectedRoute allowedRoles={["admin", "superuser"]}>
                <VCRegistryPage />
              </ProtectedRoute>
            } />

            <Route path="/introducer-program" element={
              <ProtectedRoute>
                <IntroducerProgramPage />
              </ProtectedRoute>
            } />

            {/* EchoSense CRM — referrer "My investors" (STARSEED-CRM-011).
                Dark-launched behind crm_referrer_view (default OFF; flip is
                ticket 010's recorded action). Admin/superuser admitted per
                the pack ("admins and higher see all") — the platform's
                persona redaction decides the field set either way. */}
            <Route path="/my-investors" element={
              <ProtectedRoute allowedRoles={["referrer", "admin", "superuser"]} requiredFlag="crm_referrer_view">
                <MyInvestorsPage />
              </ProtectedRoute>
            } />
            <Route path="/my-investors/:partyId" element={
              <ProtectedRoute allowedRoles={["referrer", "admin", "superuser"]} requiredFlag="crm_referrer_view">
                <InvestorDetailPage />
              </ProtectedRoute>
            } />

            <Route path="/command-center" element={
              <ProtectedRoute>
                <CommandCenterPage />
              </ProtectedRoute>
            } />

            <Route path="/partner-agreement" element={
              <ProtectedRoute>
                <PartnerAgreementPage />
              </ProtectedRoute>
            } />

            {/* FinBPM tokenized review surface — the URL token is the
                credential (finbpm-flow-004); deliberately NOT ProtectedRoute. */}
            <Route path="/partner/agreement/:token" element={<PartnerReviewPage />} />

            {/* Role-gated (STARSEED-CAPMARKETS-063): the page renders the
                member's OWN FinBPM funding instructions (/finbpm/funding/me
                409s without a countersigned agreement), so staff accounts
                only ever see an error state here. Admin funding surfaces
                live in the Command Center group. */}
            <Route path="/partner-funding" element={
              <ProtectedRoute allowedRoles={["client", "referrer"]}>
                <PartnerFundingPage />
              </ProtectedRoute>
            } />

            <Route path="/command-center/intake" element={
              <ProtectedRoute>
                <CommandCenterIntakePage />
              </ProtectedRoute>
            } />

            <Route path="/command-center/consent" element={
              <ProtectedRoute>
                <CommandCenterConsentPage />
              </ProtectedRoute>
            } />

            <Route path="/command-center/profile" element={
              <ProtectedRoute>
                <CommandCenterProfilePage />
              </ProtectedRoute>
            } />

            <Route path="/admin/command-center" element={
              <ProtectedRoute allowedRoles={["admin", "superuser"]}>
                <AdminCommandCenterPage />
              </ProtectedRoute>
            } />

            <Route path="/admin/command-center/:userId" element={
              <ProtectedRoute allowedRoles={["admin", "superuser"]}>
                <AdminCommandCenterFilePage />
              </ProtectedRoute>
            } />
            
            <Route path="/payments" element={
              <ProtectedRoute>
                <PaymentsPage />
              </ProtectedRoute>
            } />
            
            {/* /commissions + /comp-fund removed (STARSEED-CAPMARKETS-080) —
                compensation surfaces retired per attorney direction. */}

            <Route path="/partner-account" element={
              <ProtectedRoute requiredModule="monaxa">
                <PartnerAccountPage />
              </ProtectedRoute>
            } />
            <Route path="/monaxa" element={<Navigate to="/partner-account" replace />} />

            {/* /pocketoption removed (STARSEED-CAPMARKETS-080). */}

            <Route path="/analytics" element={
              <ProtectedRoute>
                <AnalyticsPage />
              </ProtectedRoute>
            } />

            <Route path="/how-to-invest" element={
              <ProtectedRoute requiredModule="how_to_invest">
                <HowToInvestPage />
              </ProtectedRoute>
            } />
            
            {/* Admin Only Routes */}
            <Route path="/users" element={
              <ProtectedRoute allowedRoles={["admin", "superuser"]}>
                <UsersPage />
              </ProtectedRoute>
            } />
            
            <Route path="/leads" element={
              <ProtectedRoute allowedRoles={["admin", "superuser"]}>
                <LeadsPage />
              </ProtectedRoute>
            } />
            
            <Route path="/lead-management" element={
              <ProtectedRoute>
                <LeadManagementPage />
              </ProtectedRoute>
            } />
            
            {/* /commission-events removed (STARSEED-CAPMARKETS-080). */}

            <Route path="/import-contacts" element={
              <ProtectedRoute allowedRoles={["admin", "superuser"]}>
                <ContactImportPage />
              </ProtectedRoute>
            } />
            
            <Route path="/settings" element={
              <ProtectedRoute allowedRoles={["admin", "superuser"]}>
                <SettingsPage />
              </ProtectedRoute>
            } />

            <Route path="/announcements" element={
              <ProtectedRoute allowedRoles={["admin", "superuser"]}>
                <AnnouncementsAdminPage />
              </ProtectedRoute>
            } />

            {/* /trial-reviews removed (STARSEED-CAPMARKETS-080). */}

            <Route path="/lifestyle" element={
              <ProtectedRoute requiredModule="lifestyle">
                <LifestylePage />
              </ProtectedRoute>
            } />

            <Route path="/lifestyle-admin" element={
              <ProtectedRoute allowedRoles={["admin", "superuser"]}>
                <LifestyleAdminPage />
              </ProtectedRoute>
            } />

            <Route path="/messages" element={
              <ProtectedRoute>
                <MessagesPage />
              </ProtectedRoute>
            } />

            <Route path="/compliance-pipeline" element={
              <ProtectedRoute allowedRoles={["admin", "superuser"]}>
                <CompliancePipelinePage />
              </ProtectedRoute>
            } />

            <Route path="/admin/investor-files" element={
              <ProtectedRoute allowedRoles={["admin", "superuser"]}>
                <AdminInvestorFilePage />
              </ProtectedRoute>
            } />

            <Route path="/mailbox-setup" element={
              <ProtectedRoute allowedRoles={["admin", "superuser"]}>
                <MailboxSetupPage />
              </ProtectedRoute>
            } />

            <Route path="/membership-fees" element={
              <ProtectedRoute allowedRoles={["admin", "superuser"]}>
                <MembershipFeesPage />
              </ProtectedRoute>
            } />

            <Route path="/dealflow" element={
              <ProtectedRoute>
                <DealFlowPage />
              </ProtectedRoute>
            } />

            <Route path="/admin/dealflow" element={
              <ProtectedRoute allowedRoles={["admin", "superuser"]}>
                <AdminDealFlowPage />
              </ProtectedRoute>
            } />

            <Route path="/admin/promo-codes" element={
              <ProtectedRoute allowedRoles={["admin", "superuser"]}>
                <AdminPromoCodesPage />
              </ProtectedRoute>
            } />

            {/* STARSEED-CAPMARKETS-134 — unresolved partner-account queue. */}
            <Route path="/admin/partner-accounts" element={
              <ProtectedRoute allowedRoles={["admin", "superuser"]}>
                <AdminPartnerAccountsPage />
              </ProtectedRoute>
            } />
            
            {/* STARSEED-CAPMARKETS-194 — enter partner balances without an
                engineer. The import route has existed since 2026-08-03 with no
                surface calling it. */}
            <Route path="/admin/balance-entry" element={
              <ProtectedRoute allowedRoles={["admin", "superuser"]}>
                <AdminBalanceEntryPage />
              </ProtectedRoute>
            } />

            {/* Participant Management Console — the external FinBPM partner's
                surface (participant-console-002). The splat hands the nested
                `<Routes>` subtree inside the shell every `/console/*` path, so
                later frontend tickets add nested routes WITHOUT editing App.js.
                This is the ONLY ticket in the pack that touches App.js. */}
            <Route path="/console/*" element={
              <PartnerRoute>
                <ParticipantConsole />
              </PartnerRoute>
            } />

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <HelpPopover />
        </BrowserRouter>
        <Toaster position="top-right" theme="dark" richColors />
        <LiveChat />
      </div>
    </AuthProvider>
      </ThemeProvider>
    </LanguageProvider>
  );
}

export default App;
